/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { FileSpreadsheet, Plus, Trash2 } from 'lucide-react';
import { FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { DocumentsTable } from '@/components/DocumentsTable';
import { GenerateDoc } from '@/components/GenerateDoc';
import { PartyPicker } from '@/components/PartyPicker';
import { Badge, Button, Empty, ErrorText, Field, Input, KV, Modal, PageHeader, Select, Spinner, Tabs } from '@/components/ui';
import { api, download } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate, fmtDateTime, fmtMoney, today } from '@/lib/format';
import { ProcedureForm, useBkDicts } from './Bankruptcy';
import { LotsPanel } from './Auctions';

type Tab = 'checklist' | 'registry' | 'pay' | 'lots' | 'documents';
const CLAIM_ST: Record<string, { l: string; t: 'slate' | 'green' | 'red' | 'amber' }> = { FILED: { l: 'заявлено', t: 'amber' }, INCLUDED: { l: 'включено', t: 'green' }, REJECTED: { l: 'отказано', t: 'red' }, WITHDRAWN: { l: 'отозвано', t: 'slate' }, REPLACED: { l: 'замена', t: 'slate' } };

export function ProcedureCardPage() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('checklist');
  const [edit, setEdit] = useState(false);
  const { data: p, isLoading, error } = useQuery({ queryKey: ['procedure', id], queryFn: () => api.get(`/bankruptcy/procedures/${id}`) });
  if (isLoading) return <Spinner />;
  if (error || !p) return <ErrorText error={error} />;
  const w = can('bankruptcy.write');
  return (
    <>
      <PageHeader title={p.debtor?.name ?? 'Процедура'} subtitle={<>{p.kindName}{p.caseNumber && ` · дело № ${p.caseNumber}`}{p.finishedAt && <Badge>завершена {fmtDate(p.finishedAt)}</Badge>}</>}
        actions={<>
          <GenerateDoc entity="procedure" entityId={id!} />
          {w && <Button variant="secondary" onClick={() => setEdit(true)}>Изменить</Button>}
        </>} />
      <div className="card mb-5 p-4"><KV items={[
        ['Дело в CRM', <Link key="m" className="text-brand-700 hover:underline" to={`/matters/${p.matterId}`}>открыть карточку дела</Link>],
        ['Суд', p.court], ['Судья', p.judge], ['Управляющий', p.managerName], ['СРО', p.sro],
        ['Введена', fmtDate(p.introducedAt)], ['Публикация ЕФРСБ', p.efrsbPublishedAt && fmtDate(p.efrsbPublishedAt)], ['Публикация «Коммерсантъ»', p.kommersantPublishedAt && fmtDate(p.kommersantPublishedAt)],
        ['Следующее заседание', p.nextHearingAt && fmtDateTime(p.nextHearingAt)],
        ['Фиксированное вознаграждение', p.fee && `${fmtMoney(p.fee.amount)} (${p.fee.months} мес. ${p.fee.days} дн. на ${fmtDate(p.fee.on)})`],
        ['Реестр всего', fmtMoney(p.registry.total)],
      ]} /></div>
      <Tabs<Tab> value={tab} onChange={setTab} tabs={[
        { id: 'checklist', label: 'Чек-лист', count: (p.checklist ?? []).filter((i: any) => !i.done).length },
        { id: 'registry', label: 'Реестр требований', count: p.claims.length }, { id: 'pay', label: 'Расчёты с кредиторами' },
        { id: 'lots', label: 'Торги' }, { id: 'documents', label: 'Документы' }]} />
      {tab === 'checklist' && <Checklist p={p} canWrite={w} />}
      {tab === 'registry' && <Registry p={p} canWrite={w} />}
      {tab === 'pay' && <Distribution p={p} canWrite={w} />}
      {tab === 'lots' && <LotsPanel matterId={p.matterId} procedureId={p.id} />}
      {tab === 'documents' && <DocumentsTable filter={{ matterId: p.matterId }} />}
      <Modal title="Изменение процедуры" open={edit} onClose={() => setEdit(false)} wide><ProcedureForm proc={p} onSaved={() => setEdit(false)} /></Modal>
    </>
  );
}

function Checklist({ p, canWrite }: { p: any; canWrite: boolean }) {
  const qc = useQueryClient();
  const [items, setItems] = useState<any[]>(p.checklist ?? []);
  const [text, setText] = useState(''); const [due, setDue] = useState('');
  const save = useMutation({ mutationFn: (list: any[]) => api.patch(`/bankruptcy/procedures/${p.id}/checklist`, { items: list.map(({ due, ...x }) => (due ? { ...x, due } : x)) }),
    onSuccess: (r: any) => { setItems(r.checklist); qc.invalidateQueries({ queryKey: ['procedure', p.id] }); qc.invalidateQueries({ queryKey: ['procedures'] }); } });
  const t = today();
  const upd = (list: any[]) => { setItems(list); save.mutate(list); };
  return (
    <div className="card">
      <ul className="divide-y divide-slate-100">
        {items.map((i, n) => (
          <li key={i.code} className="flex items-start gap-3 px-3 py-2.5">
            <input type="checkbox" className="mt-1" disabled={!canWrite} checked={i.done} onChange={() => upd(items.map((x, j) => (j === n ? { ...x, done: !x.done } : x)))} />
            <div className="min-w-0 flex-1">
              <div className={clsx('text-sm', i.done && 'text-slate-400 line-through')}>{i.text}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs">
                {canWrite ? <input type="date" className="rounded border border-slate-200 px-1 py-0.5" value={i.due ?? ''} onChange={(e) => upd(items.map((x, j) => (j === n ? { ...x, due: e.target.value || undefined } : x)))} />
                  : i.due && <span>{fmtDate(i.due)}</span>}
                {!i.done && i.due && i.due < t && <Badge tone="red">просрочено</Badge>}
                {i.done && i.doneAt && <span className="text-slate-500">выполнено {fmtDate(i.doneAt)}</span>}
              </div>
            </div>
            {canWrite && <button className="text-slate-400 hover:text-red-600" title="Удалить пункт" onClick={() => upd(items.filter((_, j) => j !== n))}><Trash2 size={15} /></button>}
          </li>
        ))}
      </ul>
      {canWrite && <div className="flex flex-wrap gap-2 border-t border-slate-200 p-3">
        <Input className="min-w-[240px] flex-1" placeholder="Новый пункт" value={text} onChange={(e) => setText(e.target.value)} />
        <Input type="date" className="w-40" value={due} onChange={(e) => setDue(e.target.value)} />
        <Button disabled={!text} onClick={() => { upd([...items, { code: `custom_${Date.now()}`, text, due: due || undefined, done: false }]); setText(''); setDue(''); }}><Plus size={16} />Добавить</Button>
      </div>}
      <ErrorText error={save.error} />
      <div className="px-3 pb-3 text-xs text-slate-500">Типовой чек-лист по 127-ФЗ. Сроки ставятся от даты введения процедуры и даты публикации; проверьте их и при необходимости поправьте.</div>
    </div>
  );
}

function Registry({ p, canWrite }: { p: any; canWrite: boolean }) {
  const { data: dicts } = useBkDicts();
  const [form, setForm] = useState<any | null>(null);
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {p.registry.byQueue.filter((q: any) => q.count).map((q: any) => (
          <div key={q.queue} className="card p-3"><div className="text-xs text-slate-500">{q.name} · {q.count}</div>
            <div className="text-sm font-semibold tabular-nums">{fmtMoney(q.principal)}</div>
            {Number(q.penalties) > 0 && <div className="text-xs text-slate-500">санкции {fmtMoney(q.penalties)}</div>}
            {Number(q.repaid) > 0 && <div className="text-xs text-green-700">погашено {fmtMoney(q.repaid)}</div>}</div>
        ))}
      </div>
      <div className="card">
        <div className="flex justify-between gap-2 border-b border-slate-200 p-2">
          <Button size="sm" variant="secondary" onClick={() => download(`/bankruptcy/procedures/${p.id}/registry.xlsx`)}><FileSpreadsheet size={14} />Реестр в Excel</Button>
          {canWrite && <Button size="sm" onClick={() => setForm({})}>Добавить требование</Button>}
        </div>
        {!p.claims.length ? <Empty>Требований нет</Empty> : (
          <div className="overflow-x-auto"><table className="w-full">
            <thead className="bg-slate-50"><tr><th className="th">Кредитор</th><th className="th">Очередь</th><th className="th text-right">Основной долг</th><th className="th text-right">Санкции</th><th className="th text-right">Погашено</th><th className="th">Акт</th><th className="th">Статус</th></tr></thead>
            <tbody>{p.claims.map((c: any) => (
              <tr key={c.id} className={clsx('border-t border-slate-100', canWrite && 'cursor-pointer hover:bg-slate-50')} onClick={() => canWrite && setForm(c)}>
                <td className="td">{c.creditorName}{c.secured && <Badge tone="blue">залог</Badge>}</td>
                <td className="td">{dicts?.queues[c.queue]}</td>
                <td className="td text-right tabular-nums">{fmtMoney(c.principal)}</td><td className="td text-right tabular-nums">{fmtMoney(c.penalties)}</td>
                <td className="td text-right tabular-nums">{fmtMoney(c.repaid)}</td>
                <td className="td text-xs">{c.courtAct}{c.includedAt && <div>{fmtDate(c.includedAt)}</div>}</td>
                <td className="td"><Badge tone={CLAIM_ST[c.status].t}>{CLAIM_ST[c.status].l}</Badge></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
      {p.registry.votes.length > 0 && (
        <div className="card p-4">
          <div className="mb-2 text-sm font-medium">Голоса на собрании (включённые требования 3-й очереди и залоговые, без санкций): {fmtMoney(p.registry.votesTotal)}</div>
          {p.registry.votes.map((v: any) => (
            <div key={v.name} className="mb-1.5">
              <div className="flex justify-between text-sm"><span>{v.name}</span><span className="tabular-nums">{String(v.pct).replace('.', ',')}%</span></div>
              <div className="h-1.5 rounded bg-slate-100"><div className="h-1.5 rounded bg-brand-500" style={{ width: `${v.pct}%` }} /></div>
            </div>
          ))}
        </div>
      )}
      <Modal title={form?.id ? 'Требование' : 'Новое требование'} open={!!form} onClose={() => setForm(null)} wide>{form && <ClaimForm p={p} claim={form} onSaved={() => setForm(null)} />}</Modal>
    </div>
  );
}

function ClaimForm({ p, claim, onSaved }: { p: any; claim: any; onSaved: () => void }) {
  const qc = useQueryClient();
  const { data: dicts } = useBkDicts();
  const [f, setF] = useState<any>({ creditorId: claim.creditorId ?? null, creditorName: claim.creditorName ?? '', queue: claim.queue ?? 'Q3', principal: claim.principal ?? '', penalties: claim.penalties ?? '0',
    secured: claim.secured ?? false, collateral: claim.collateral ?? '', basis: claim.basis ?? '', filedAt: claim.filedAt ?? '', courtAct: claim.courtAct ?? '', includedAt: claim.includedAt ?? '', status: claim.status ?? 'FILED', note: claim.note ?? '' });
  const inv = () => { qc.invalidateQueries({ queryKey: ['procedure', p.id] }); onSaved(); };
  const save = useMutation({ mutationFn: () => {
    const n = (v: string) => (v === '' ? null : v);
    const b = { ...f, principal: String(f.principal).replace(',', '.'), penalties: String(f.penalties || 0).replace(',', '.'), collateral: n(f.collateral), basis: n(f.basis), filedAt: n(f.filedAt), courtAct: n(f.courtAct), includedAt: n(f.includedAt), note: n(f.note) };
    return claim.id ? api.patch(`/bankruptcy/claims/${claim.id}`, b) : api.post(`/bankruptcy/procedures/${p.id}/claims`, b);
  }, onSuccess: inv });
  const del = useMutation({ mutationFn: () => api.del(`/bankruptcy/claims/${claim.id}`), onSuccess: inv });
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });
  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };
  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
      <Field label="Кредитор из базы" className="sm:col-span-2"><PartyPicker value={f.creditorId} valueLabel={f.creditorName} onChange={(id, name) => setF({ ...f, creditorId: id, creditorName: name ?? f.creditorName })} placeholder="Необязательно" /></Field>
      <Field label="Наименование кредитора" className="sm:col-span-2"><Input required value={f.creditorName} onChange={set('creditorName')} /></Field>
      <Field label="Очередь"><Select value={f.queue} onChange={set('queue')} options={Object.entries(dicts?.queues ?? {}).map(([value, label]) => ({ value, label }))} /></Field>
      <Field label="Статус"><Select value={f.status} onChange={set('status')} options={Object.entries(CLAIM_ST).map(([value, s]) => ({ value, label: s.l }))} /></Field>
      <Field label="Основной долг, ₽"><Input required inputMode="decimal" value={f.principal} onChange={set('principal')} /></Field>
      <Field label="Финансовые санкции, ₽" hint="Учитываются отдельно (п. 3 ст. 137)"><Input inputMode="decimal" value={f.penalties} onChange={set('penalties')} /></Field>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.secured} onChange={(e) => setF({ ...f, secured: e.target.checked })} />Обеспечено залогом</label>
      {f.secured && <Field label="Предмет залога"><Input value={f.collateral} onChange={set('collateral')} /></Field>}
      <Field label="Основание" className="sm:col-span-2"><Input value={f.basis} onChange={set('basis')} /></Field>
      <Field label="Дата заявления"><Input type="date" value={f.filedAt} onChange={set('filedAt')} /></Field>
      <Field label="Дата включения"><Input type="date" value={f.includedAt} onChange={set('includedAt')} /></Field>
      <Field label="Судебный акт" className="sm:col-span-2"><Input value={f.courtAct} onChange={set('courtAct')} placeholder="Определение от 01.10.2026" /></Field>
      <div className="sm:col-span-2"><ErrorText error={save.error ?? del.error} /></div>
      <div className="flex justify-between sm:col-span-2">
        {claim.id ? <Button type="button" variant="ghost" onClick={() => del.mutate()}>Удалить</Button> : <span />}
        <Button disabled={save.isPending}>Сохранить</Button>
      </div>
    </form>
  );
}

function Distribution({ p, canWrite }: { p: any; canWrite: boolean }) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const run = useMutation({ mutationFn: (apply: boolean) => api.post(`/bankruptcy/procedures/${p.id}/distribute`, { amount: amount.replace(',', '.'), apply }),
    onSuccess: (r: any) => { if (r.applied) qc.invalidateQueries({ queryKey: ['procedure', p.id] }); } });
  const r: any = run.data;
  return (
    <div className="card space-y-4 p-4">
      <div className="text-sm text-slate-600">Порядок (ст. 134, 137, 142): 1-я очередь, 2-я очередь, основной долг 3-й очереди, финансовые санкции 3-й очереди, требования за реестром. Внутри группы - пропорционально. Залоговые требования гасятся из выручки от предмета залога отдельно.</div>
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Сумма к распределению, ₽"><Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
        <Button variant="secondary" disabled={!amount || run.isPending} onClick={() => run.mutate(false)}>Рассчитать</Button>
        {canWrite && r && !r.applied && <Button disabled={run.isPending} onClick={() => run.mutate(true)}>Провести погашение в реестре</Button>}
      </div>
      <ErrorText error={run.error} />
      {r && <>
        <table className="w-full text-sm"><thead className="bg-slate-50"><tr><th className="th">Группа</th><th className="th text-right">Требования</th><th className="th text-right">Выплата</th><th className="th text-right">%</th></tr></thead>
          <tbody>{r.groups.map((g: any) => <tr key={g.group} className="border-t border-slate-100"><td className="td">{g.group}</td><td className="td text-right tabular-nums">{fmtMoney(g.due)}</td><td className="td text-right tabular-nums">{fmtMoney(g.paid)}</td><td className="td text-right">{String(g.pct).replace('.', ',')}</td></tr>)}</tbody></table>
        <table className="w-full text-sm"><thead className="bg-slate-50"><tr><th className="th">Кредитор</th><th className="th text-right">Выплата</th></tr></thead>
          <tbody>{r.perClaim.map((x: any) => <tr key={x.id} className="border-t border-slate-100"><td className="td">{x.creditorName}</td><td className="td text-right tabular-nums">{fmtMoney(x.amount)}</td></tr>)}</tbody></table>
        {Number(r.remainder) > 0 && <div className="text-sm">Остаток после расчётов: <b>{fmtMoney(r.remainder)}</b></div>}
        {r.applied && <div className="rounded bg-green-50 px-3 py-2 text-sm text-green-800">Погашения записаны в реестр.</div>}
      </>}
    </div>
  );
}
